import React, { useState, useEffect } from "react";
import { Form, Input, Upload, message, DatePicker, Radio, Switch, Card, Button as AntButton } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { createEvent, updateEvent, fetchEventImageBankDefaults } from "../../../../api/events";
import { uploadImage } from "../../../../api/service";
import colors from "../../../../share/constants/constants";
import Button from "../../../../components/common/button";
import dayjs from "dayjs";
import ImageBankSelector from "./ImageBankSelector";

const EventsForm = ({ onSuccess, defaultValues, isEdit }) => {
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState(defaultValues?.eventImage || "");
  const [form] = Form.useForm();
  
  // Image bank state - default images that persist
  const [imageBank, setImageBank] = useState([]);
  const [imageBankItems, setImageBankItems] = useState(
    defaultValues?.imageBankItems || []
  );
  
  // Initialize default images (these would typically come from backend)
  useEffect(() => {
    // Load default images from backend
    const loadDefaultImages = async () => {
      try {
        const response = await fetchEventImageBankDefaults();
        if (response?.data && Array.isArray(response.data)) {
          setImageBank(response.data);
        }
      } catch (error) {
        console.error("Error loading default images:", error);
        // Fallback to empty array if API fails
      }
    };

    // Also include images from existing items
    const existingImages = defaultValues?.imageBankItems?.map(item => ({
      url: item.imageUrl,
      id: item.imageUrl,
      isDefault: true,
    })) || [];
    
    // Merge existing images with defaults (avoid duplicates)
    if (existingImages.length > 0) {
      setImageBank(existingImages);
    } else {
      loadDefaultImages();
    }
    
    setImageBankItems(defaultValues?.imageBankItems || []);
  }, [defaultValues]);

  // Handle new image upload to bank
  const handleImageBankUpload = (newImage) => {
    setImageBank((prev) => [...prev, newImage]);
  };

  const beforeUpload = (file) => {
    const isJpgOrPng =
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/webp";
    const isLt1M = file.size / 1024 / 1024 <= 1;

    if (!isJpgOrPng) {
      message.error({
        content: 'You can only upload JPG, PNG, or Webp file formats!',
        style: { marginTop: '20px' },
        duration: 4,
      });
    }
    
    if (!isLt1M) {
      const actualSize = (file.size / 1024 / 1024).toFixed(2);
      message.error({
        content: `Image size (${actualSize} MB) exceeds the 1 MB limit. Please select a smaller one.`,
        style: { marginTop: '20px' },
        duration: 4,
      });
    }
    
    return { isJpgOrPng, isLt1M };
  };

  const handleImageUpload = async (info) => {
    try {
      const { file } = info;
      const validImage = beforeUpload(file);
      
      if (validImage.isJpgOrPng && validImage.isLt1M) {
        const formData = new FormData();
        formData.append("image", file);
        const response = await uploadImage(formData);
        if (response) {
          setImageUrl(response.url);
          form.setFieldValue("eventImage", response.url);
          message.success("Image uploaded successfully");
        }
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      message.error("Error while uploading image");
    }
  };

  const onFinish = async (values) => {
    if (!imageUrl) {
      message.error("Please upload an image");
      return;
    }

    setLoading(true);
    try {
      // Format the date and time
      const eventDateTime = values.eventFullDateTime;
      const date = dayjs(eventDateTime).format("YYYY-MM-DD");
      const fullDateTime = dayjs(eventDateTime).toISOString();

      // Prepare image bank items with proper order (filter out null/undefined and incomplete items)
      const preparedImageBankItems = imageBankItems
        .filter(item => item && item.text && item.imageUrl) // Only include complete items
        .map((item, index) => ({
          text: item.text,
          imageUrl: item.imageUrl,
          order: index,
        }));

      const payload = {
        ...values,
        date: date,
        eventFullDateTime: fullDateTime,
        eventImage: imageUrl,
        eventLocation: {
          locationName: values.locationName,
          locationUrl: values.locationUrl,
        },
        isActive: values.isActive !== undefined ? values.isActive : true,
        imageBankItems: preparedImageBankItems.length > 0 ? preparedImageBankItems : undefined,
      };

      // Remove the individual location fields as they're now nested
      delete payload.locationName;
      delete payload.locationUrl;

      let response;
      if (isEdit && defaultValues?._id) {
        response = await updateEvent(defaultValues._id, payload);
      } else {
        response = await createEvent(payload);
      }

      if (response) {
        message.success(`Event ${isEdit ? "updated" : "created"} successfully!`);
        form.resetFields();
        setImageUrl("");
        setImageBankItems([]);
        onSuccess && onSuccess();
      }
    } catch (error) {
      console.error(`Error ${isEdit ? "updating" : "creating"} event:`, error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="">
      <h3 className="text-lg font-semibold mb-4">
        {isEdit ? "Edit Event" : "Create New Event"}
      </h3>
      <Form
        form={form}
        onFinish={onFinish}
        layout="vertical"
        initialValues={{
          ...defaultValues,
          eventFullDateTime: defaultValues?.eventFullDateTime ? dayjs(defaultValues.eventFullDateTime) : null,
          locationName: defaultValues?.eventLocation?.locationName,
          locationUrl: defaultValues?.eventLocation?.locationUrl,
          isActive: defaultValues?.isActive !== undefined ? defaultValues.isActive : true,
        }}
      >
        <Form.Item
          name="eventTitle"
          label="Event Title"
          rules={[
            {
              required: true,
              message: "Please enter event title",
            },
          ]}
        >
          <Input placeholder="Enter event title" />
        </Form.Item>

        <Form.Item
          name="eventDescription"
          label="Event Description"
          rules={[
            {
              required: true,
              message: "Please enter event description",
            },
          ]}
        >
          <Input.TextArea 
            rows={4} 
            placeholder="Enter event description"
          />
        </Form.Item>

        <Form.Item
          name="eventImage"
          label="Event Image"
          rules={[
            {
              required: true,
              message: "Please upload an image",
            },
          ]}
        >
          <Upload
            listType="picture-card"
            showUploadList={false}
            beforeUpload={() => false}
            onChange={handleImageUpload}
            accept="image/jpeg,image/png,image/webp"
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt="uploaded"
                style={{ width: "100%" }}
              />
            ) : (
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>Upload</div>
              </div>
            )}
          </Upload>
        </Form.Item>

        <Form.Item
          name="eventFullDateTime"
          label="Event Date & Time"
          rules={[
            {
              required: true,
              message: "Please select event date and time",
            },
          ]}
        >
          <DatePicker
            showTime={{ format: "HH:mm" }}
            format="YYYY-MM-DD HH:mm"
            placeholder="Select date and time"
            className="w-full"
          />
        </Form.Item>

        <Form.Item
          name="locationName"
          label="Location Name"
          rules={[
            {
              required: true,
              message: "Please enter location name",
            },
          ]}
        >
          <Input placeholder="Enter location name" />
        </Form.Item>

        <Form.Item
          name="locationUrl"
          label="Location URL"
          rules={[
            {
              required: true,
              message: "Please enter location URL",
            },
            {
              type: "url",
              message: "Please enter a valid URL",
            },
          ]}
        >
          <Input placeholder="Enter location URL (e.g., Google Maps link)" />
        </Form.Item>

        <Form.Item
          name="clickActionType"
          label="Click Action Type"
          rules={[
            {
              required: true,
              message: "Please select click action type",
            },
          ]}
        >
          <Radio.Group>
            <Radio value="service">Service</Radio>
            <Radio value="weblink">Web Link</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="clickActionValue"
          label="Click Action Value"
          rules={[
            {
              required: true,
              message: "Please enter click action value",
            },
          ]}
        >
          <Input placeholder="Enter service ID or web link URL" />
        </Form.Item>

        <Form.Item
          name="isActive"
          label="Active Status"
          valuePropName="checked"
        >
          <Switch 
            checkedChildren="Active" 
            unCheckedChildren="Inactive"
          />
        </Form.Item>

        {/* Image Bank Section - Up to 3 Image + Text items */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="mb-6">
            <h4 className="text-base font-semibold mb-2 text-gray-800">Image Bank Items</h4>
            <p className="text-sm text-gray-600">
              Add up to 3 items, each with text and an image from the image bank. Default images are suggested, but you can upload new ones.
            </p>
          </div>
          
          <div className="space-y-5">
            {[0, 1, 2].map((slotIndex) => {
              const item = imageBankItems[slotIndex];
              const isActive = item !== undefined && item !== null;
              
              return (
                <Card
                  key={slotIndex}
                  title={
                    <span className="text-base font-medium text-gray-700">
                      Item {slotIndex + 1}
                    </span>
                  }
                  extra={
                    isActive && (
                      <AntButton
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          const updated = [...imageBankItems];
                          updated.splice(slotIndex, 1);
                          // Fill with null to maintain array positions
                          while (updated.length < 3) {
                            updated.push(null);
                          }
                          // Remove nulls from end
                          while (updated.length > 0 && updated[updated.length - 1] === null) {
                            updated.pop();
                          }
                          setImageBankItems(updated);
                        }}
                        size="small"
                      >
                        Remove
                      </AntButton>
                    )
                  }
                  className="shadow-sm"
                  styles={{
                    body: {
                      padding: '20px',
                    }
                  }}
                >
                  {isActive ? (
                    <div className="space-y-5">
                      <Form.Item
                        label={<span className="text-sm font-medium">Text</span>}
                        required
                        className="mb-0"
                      >
                        <Input
                          placeholder="Enter text for this item"
                          value={item.text || ""}
                          onChange={(e) => {
                            const updated = [...imageBankItems];
                            updated[slotIndex] = { ...updated[slotIndex], text: e.target.value, imageUrl: updated[slotIndex].imageUrl || "" };
                            setImageBankItems(updated);
                          }}
                          size="large"
                        />
                      </Form.Item>

                      <Form.Item
                        label={<span className="text-sm font-medium">Image</span>}
                        required
                        className="mb-0"
                      >
                        <ImageBankSelector
                          value={item.imageUrl}
                          defaultImages={imageBank}
                          onImageUpload={handleImageBankUpload}
                          onChange={(url) => {
                            const updated = [...imageBankItems];
                            updated[slotIndex] = { ...updated[slotIndex], text: updated[slotIndex].text || "", imageUrl: url };
                            setImageBankItems(updated);
                          }}
                        />
                      </Form.Item>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <AntButton
                        type="dashed"
                        onClick={() => {
                          const updated = [...imageBankItems];
                          // Fill gaps with null if needed
                          while (updated.length < slotIndex) {
                            updated.push(null);
                          }
                          updated[slotIndex] = { text: "", imageUrl: "" };
                          setImageBankItems(updated);
                        }}
                        icon={<PlusOutlined />}
                        disabled={imageBankItems.length >= 3}
                        size="large"
                        className="w-full"
                      >
                        Add Item {slotIndex + 1}
                      </AntButton>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>

        <Form.Item>
          <Button
            color={colors.primaryBlue}
            title={isEdit ? "Update Event" : "Create Event"}
            htmlType="submit"
            loading={loading}
            className="w-full"
          />
        </Form.Item>
      </Form>
    </div>
  );
};

export default EventsForm;